import { Group, Text } from '@mantine/core';
import { IconFlask2 } from '@tabler/icons-react';
import { InfoNote } from './info-note';
import classes from './sample-data-banner.module.css';

const FULL_TEXT =
  'The events behind this screen are not collected yet, so it reads hand-written fixtures. It exists to settle layout and hierarchy before that data arrives. Wiring it means swapping its fixture import for an api.get call and removing this strip and the sidebar badge in the same change — which is what Funnel has already had done to it.';

interface SampleDataBannerProps {
  /** What this particular screen would read once it is wired. */
  detail?: string;
}

/**
 * The standing warning on the analytics screens still reading fixtures —
 * Drop-off, Signals and Journeys. Funnel is wired and does not carry it.
 *
 * `dashboard.tsx` refuses to render invented numbers, on the grounds that a
 * "0 users" tile cannot be told apart from a real empty database. Those three
 * screens break that rule on purpose — a design pass has nothing to look at
 * otherwise.
 *
 * It used to be a full Alert, which made the loudest thing on every screen a
 * caveat rather than the data. A strip keeps it unmissable without letting it
 * lead; the sidebar badges cover the case where a screenshot crops it off, and
 * the argument in full sits behind the info icon.
 */
export function SampleDataBanner({ detail }: SampleDataBannerProps) {
  return (
    <Group gap="xs" wrap="nowrap" className={classes.strip}>
      <IconFlask2 size={15} stroke={1.8} className={classes.icon} />
      <Text size="xs" fw={600} className={classes.label}>
        Sample data
      </Text>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {detail ?? 'Every figure on this screen is invented. Nothing is wired.'}
      </Text>
      <InfoNote label="About the sample data">{FULL_TEXT}</InfoNote>
    </Group>
  );
}
